/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
} from '@mui/material'
import { useOrgan } from '../../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../../layouts/PermanentSidebarMUI'
import { useMutation } from '@tanstack/react-query'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import { useNavigate } from 'react-router-dom'
import useLoader from '../../../../hooks/useLoader'
import useNotification from '../../../../hooks/useNotification'
import useForm from '../../../../hooks/useForm'
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../../utils/objectUtils'
import { reportProtocolTransactionFields } from '../../../../constants/forms/almoxarifado/reportFields'
import Form from '../../../../components/forms/Form'
import SimpleBody from '../../../../components/forms/SimpleBody'
import { useCallback, useEffect } from 'react'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import { transformFiltersForUrl } from '../../../../utils/transformFiltersForUrl'
import { downloadPdf } from '../../../../utils/downloadPdf'

export const DialogExportProtocolTransaction = ({ data, open, onClose, routeOverride = 'protocolTransaction' }) => {
  const navigate = useNavigate()
  const { get: baseServiceGet, formErrors } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()

  const [reportSection] = reportProtocolTransactionFields

  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues([reportSection]),
    [reportSection]
  )

  const usersSelect = useRemoteSelectOptions({
    queryKey: ['users'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.users.get, params)
      return response.result
    },
    fieldId: 'report.user_id',
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (user) => ({ value: user.id, label: user.name }),
  })

  const selects = {
    'report.user_id': usersSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const exportProtocol = useMutation({
    mutationFn: async ({ transactionId, data }) => {
      showLoader()
      const params = {}
      const response = await baseServiceGet(
        entities.reports[routeOverride](transactionId),
        params,
        { ...data }
      )
      return response
    },
    onSuccess: async (response) => {
      // Compatibilidade com resposta síncrona (200 com URL direta)
      if (response?.status === 200 && typeof response.result === 'string') {
        await downloadPdf(response.result)
        onClose()
        return
      }

      // Resposta assíncrona (202 com export_id)
      if (response?.status === 202 || response?.status === 200) {
        const exportId = response.result?.export_id

        if (!exportId) {
          showNotification('error', 'Não foi possível iniciar a geração do protocolo.')
          return
        }

        showNotification(
          'info',
          'Processamento do protocolo iniciado em segundo plano.'
        )

        // Polling: aguarda o job terminar e baixa o PDF
        for (let i = 0; i < 60; i++) {
          await new Promise((r) => setTimeout(r, 2000))

          try {
            const check = await baseServiceGet(
              entities.reports.checkStatus(exportId)
            )
            const res = check?.result ?? check?.data?.result

            if (res?.status === 'completed' && res?.file_path) {
              await downloadPdf(res.file_path)
              showNotification('success', 'Protocolo gerado com sucesso!')
              onClose()
              return
            }

            if (res?.status === 'failed' || res?.status === 'cancelled') {
              showNotification(
                'error',
                res?.message || 'Falha ao gerar o protocolo.'
              )
              return
            }
          } catch (err) {
            console.error('Erro no polling:', err)
          }
        }

        showNotification('error', 'Tempo esgotado ao gerar o protocolo.')
      }
    },
    onError: (error) => {
      console.error('Erro ao gerar protocolo:', error)
      showNotification(
        'error',
        error.response?.data?.message || 'Erro ao gerar o protocolo.'
      )
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = async () => {
    try {
      const payload = {
        responsible: formData.report.user_id?.label,
        cpf_cnpj: formData.report.cpf_cnpj,
      }

      await exportProtocol.mutateAsync({
        transactionId: data.id,
        data: payload,
      })
    } catch (error) {
      console.error('Erro ao exportar protocolo:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primaryColor,
          color: '#FFFFFF',
        }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Imprimir Protocolo
        </DialogTitle>
      </Box>
      <DialogContent>
        <div className="container-fluid p-1">
          <Form
            initialFormData={formData}
            onSubmit={handleSubmit}
            textSubmit="Imprimir"
            textLoadingSubmit="Imprimindo..."
            showButtons={false}>
            {() => (
              <div key={reportSection.section}>
                <SimpleBody
                  fields={reportSection.fields}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>
            )}
          </Form>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose()
            resetForm()
          }}
          color="error">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary">
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  )
}